// The AI button (release 6.1, Kareem 4 Nov: "the AI button sits in the same place on every page"). One button, drawn
// by the shell at the top right of every page (desktop: the page header's corner, the look of Today's "Capture or ask
// anything"; phone: the top bar's corner), never by the page. Each page says what it is about with useAiScope (a
// space, a track, an activity, a folder, a file, the Research Center); the button opens the one assistant drawer
// scoped to that, and the drawer's chip still widens it to everything. Today's is Quick capture: a new conversation for
// "what happened". The Assistant page is the assistant itself, so it has none.
//
// One AI button on screen (6.3, Kareem 5 Nov: two side by side was too much AI): it is always the top-right one of
// what you are looking at. The page's corner normally; while the activity panel is docked, the panel's header (its
// sphere asks about that activity), and the page's steps aside. The AI panel, once open, is the AI itself. On the
// activity's full page the button is the sphere alone, in line with the page's actions.
import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { Orb } from '../ui/motion';
import { useAssistantPanel, type PanelOpen } from './Panel';
import { useDock } from '../shell/Dock';

/** What the page is about. capture: Today's Quick capture. sphere: the button is the violet sphere alone (no words). */
export type AiScope = Pick<PanelOpen, 'spaceId' | 'trackId' | 'activityId' | 'folderId' | 'fileId' | 'purpose' | 'label'> & { capture?: boolean; sphere?: boolean };

const Ctx = createContext<{ scope: AiScope | null; set: (s: AiScope | null) => void }>({ scope: null, set: () => undefined });

export function AiScopeProvider({ children }: { children: ReactNode }) {
  const [scope, set] = useState<AiScope | null>(null);
  const value = useMemo(() => ({ scope, set }), [scope]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/** A page says what the AI button is about while it is on screen (null: Everything). */
export function useAiScope(scope: AiScope | null, enabled = true) {
  const { set } = useContext(Ctx);
  const now = useRef(scope);
  now.current = scope;
  const key = JSON.stringify(scope);
  useEffect(() => { if (!enabled) return; set(now.current); return () => set(null); }, [key, set, enabled]);
}

/** What the page on screen is about (the AI panel follows it while docked, unless pinned). */
export const usePageScope = () => useContext(Ctx).scope;

/** Violet (violet is the AI's): "Ask AI", or on Today "Capture or ask anything". */
export const AI_LABEL = 'Ask AI';
export const CAPTURE_LABEL = 'Capture or ask anything';

/**
 * The button in a sheet's header, for a sheet that is the page itself (a file, a meeting). The shell's button steps
 * aside while a sheet is open (it never covers one, 6.2), so the sheet carries it, asking about the same thing.
 */
export function SheetAiButton() {
  const { scope } = useContext(Ctx);
  const panel = useAssistantPanel();
  const go = () => { const { capture: _c, sphere: _s, ...o } = scope ?? {}; panel.open(o); };
  return <button type="button" className="aibtn sm" onClick={go}><span className="o"><Orb size={12} /></span>{AI_LABEL}</button>;
}

/** The AI button as the violet sphere alone: the name is its label and its tooltip (the activity panel, the activity page). */
export function AiSphere({ onClick, sm = false, about }: { onClick: () => void; sm?: boolean; about?: string | null }) {
  return (
    <button type="button" className={`aibtn sphere${sm ? ' sm' : ''}`} aria-label={AI_LABEL} title={about ? `${AI_LABEL} about ${about}` : AI_LABEL} onClick={onClick}>
      <span className="o"><Orb size={sm ? 12 : 14} /></span>
    </button>
  );
}

/** Ask about what is on screen: the panel (or the phone sheet) looking at this page, or Capture on Today. */
export function useAskHere(): { go: () => void; label: string } {
  const { scope } = useContext(Ctx);
  const panel = useAssistantPanel();
  const go = () => {
    if (scope?.capture) { panel.capture(); return; }
    const { capture: _c, sphere: _s, ...o } = scope ?? {};
    panel.open(o);
  };
  return { go, label: scope?.capture ? CAPTURE_LABEL : AI_LABEL };
}

/**
 * The button, where the shell puts it. Not on the Assistant page (it is the assistant). On the phone there is no
 * top-right button (Kareem, 5 Nov: one AI button): the orb in the tab bar asks about the screen (PhoneShell).
 */
export function AiButton({ phone }: { phone: boolean }) {
  const { scope } = useContext(Ctx);
  const panel = useAssistantPanel();
  const { pathname } = useLocation();
  const dock = useDock();
  if (phone || pathname.startsWith('/assistant')) return null;
  // The activity panel docked beside the page: its own sphere is the one (one AI button on screen).
  if (!phone && dock?.wide && dock.activityId) return null;
  const go = () => {
    if (scope?.capture) { panel.capture(); return; }
    const { capture: _c, sphere: _s, ...o } = scope ?? {};
    panel.open(o);
  };
  const label = scope?.capture ? CAPTURE_LABEL : AI_LABEL;
  if (scope?.sphere) return <div className="aislot sphere"><AiSphere onClick={go} about={scope.label} /></div>;
  return <div className={`aislot${scope?.capture ? ' wide' : ''}`}><button type="button" className="aibtn" onClick={go}><span className="o"><Orb size={14} /></span>{label}</button></div>;
}
